import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader2, KeyRound, ArrowRight } from "lucide-react";
import { useGuestAuth } from "@/context/GuestAuthContext";
import { pickLocale } from "@/lib/helpers";

export default function PasswordModal({ open, onOpenChange, apartment }) {
  const { t, i18n } = useTranslation();
  const { unlock } = useGuestAuth();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setPin("");
      setErr("");
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!pin || loading) return;
    setLoading(true);
    const res = await unlock(apartment.id, pin);
    setLoading(false);
    if (res.ok) {
      onOpenChange(false);
      navigate(`/appartamento/${apartment.slug}`);
    } else {
      setErr(t("apartment.wrong_password"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`password-modal-${apartment.slug}`}
        className="max-w-[440px] bg-ivory-50 border-ink-200/40 rounded-3xl shadow-elevated"
      >
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-full bg-bronze-50 flex items-center justify-center mb-2">
            <KeyRound strokeWidth={1.4} className="w-5 h-5 text-bronze-400" />
          </div>
          <DialogTitle className="text-center font-editorial italic text-[32px] leading-tight text-ink-900">
            {pickLocale(apartment.name, i18n.language)}
          </DialogTitle>
          <div className="flex justify-center py-1">
            <span className="ornament" />
          </div>
          <DialogDescription className="text-center text-ink-500 text-[13px] leading-relaxed">
            {t("apartment.unlock_hint")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3 pt-3">
          <Input
            autoFocus
            data-testid="password-input"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            placeholder={t("apartment.pin_placeholder")}
            value={pin}
            onChange={(e) => setPin(e.target.value.toUpperCase())}
            className="h-12 text-center tracking-[0.28em] text-base font-semibold bg-white border-ink-200/60 focus-visible:ring-bronze-400"
          />
          {err && (
            <p data-testid="password-error" className="text-sm text-red-700 text-center">{err}</p>
          )}
          <button
            data-testid="password-submit"
            type="submit"
            disabled={loading}
            className="mt-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-bronze-400 text-white font-semibold tracking-wide shadow-soft hover:bg-bronze-500 active:scale-[0.98] transition disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                {t("apartment.unlock_button")}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
